import React from 'react';
import { ErrorState } from '../../../../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../../../../../../CommonComponents/OPL/Box/Box';
import { ColumnChart } from '../../../../../../../../CommonComponents/OPL/charts/ColumnChart/ColumnChart';
import { HtmlTable } from '../../../../../../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { Text } from '../../../../../../../../CommonComponents/OPL/Text/Text';
import { formatCurrency } from '../../../../../../../../CommonUtils/formatNumber';
import { SocialSecurityStrategy } from '../../api/useAnnualBenefitsSummaryQuery';
import { BenefitsModalLoading } from './BenefitsModal.loading';
import { ModalWrapper } from './components/ModalWrapper/ModalWrapper';
import { useBenefitsModal } from './useBenefitsModal';

interface Props {
  onClose(): void;
  strategy: SocialSecurityStrategy;
}

export const BenefitsModal = ({ strategy, onClose }: Props) => {
  const { isFetching, data, error, refetch } = useBenefitsModal({ strategy });

  if (isFetching) {
    return <BenefitsModalLoading onClose={onClose} />;
  }

  if (error) {
    return (
      <ModalWrapper onClose={onClose}>
        <Box display="flex" alignItems="center" justifyContent="center" py="24" height="100%">
          <ErrorState onAction={refetch} />
        </Box>
      </ModalWrapper>
    );
  }

  if (!data) {
    return null;
  }

  const { chart, ages, principalRow, spouseRow, years, principalName, spouseName } = data;

  const series = [
    ...(chart.principal && principalName
      ? [{ name: principalName, data: chart.principal.benefits }]
      : []),
    ...(chart.spouse && spouseName ? [{ name: spouseName, data: chart.spouse.benefits }] : []),
  ];

  return (
    <ModalWrapper onClose={onClose}>
      <>
        <Box mb="24" p="24">
          <ColumnChart
            series={series}
            categories={years}
            isStacked
            yAxisType="currency"
            customSettings={{
              tooltip: {
                shared: true,

                formatter() {
                  let tooltip = `
                      <span style="font-weight: 600">
                        Year: ${this.x}
                      </span><table>
                  `;

                  if ((this as any).points) {
                    (this as any).points.forEach(({ y, color }, index) => {
                      tooltip += `
                          <tr>
                          <td>
                              <span style="color:${color}">\u25CF</span>
                          </td>
                          <td>
                              <span style="font-weight: 600">
                              ${series[index]?.name}: ${formatCurrency(Number(y), {
                        decimalPlaces: 0,
                      })}
                              </span>
                          </td>
                          </tr>
                      `;
                    });
                  }

                  return `${tooltip}</table>`;
                },
              },
            }}
          />
        </Box>

        <HtmlTable variant="embedded" pb="24">
          <table>
            <thead>
              <tr>
                <th>
                  <Text variant="smallCaps">Year</Text>
                </th>

                {years.map(header => (
                  <th key={header}>
                    <Text variant="smallCaps">{header}</Text>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>
                  <Text>
                    {principalName && spouseName
                      ? `Ages (${principalName} / ${spouseName})`
                      : 'Ages'}
                  </Text>
                </td>
                {ages.map(age => (
                  <td key={age}>
                    <Text>{age}</Text>
                  </td>
                ))}
              </tr>

              {principalName && principalRow && (
                <tr>
                  <td>
                    <Text>{principalName}</Text>
                  </td>

                  {principalRow.map((cell, index) => (
                    <td key={index}>
                      <Text>{cell}</Text>
                    </td>
                  ))}
                </tr>
              )}

              {spouseName && spouseRow && (
                <tr>
                  <td>
                    <Text>{spouseName}</Text>
                  </td>

                  {spouseRow.map((cell, index) => (
                    <td key={index}>
                      <Text>{cell}</Text>
                    </td>
                  ))}
                </tr>
              )}
            </tbody>
          </table>
        </HtmlTable>
      </>
    </ModalWrapper>
  );
};
